import { ContentCopy } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import type { ElementType } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDeleteTVBrand } from '~/api/tv-brands/delete';
import { useDuplicateTVBrand } from '~/api/tv-brands/duplicate';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { NetworkTvBrands__TvBrand as TVBrand } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  tvBrand: TVBrand;
  Icon?: ElementType;
}

export const Menu = ({ tvBrand, ...cellProps }: MenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const navigate = useNavigate();
  const link = useLink();

  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<TVBrand>();
  const tvBrandActionProps = mergeGridActionProps(moreTableActionProps(tvBrand), cellProps);
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteTvBrand] = useDeleteTVBrand();
  const [duplicateTvBrand] = useDuplicateTVBrand();

  return (
    <>
      <ActionButton {...tvBrandActionProps} />
      <MuiMenu {...moreMenuProps}>
        <MenuItem
          onClick={async () => {
            const result = await duplicateTvBrand({
              variables: {
                input: { networkId: currentNetwork.id, tvBrandId: tvBrand.id },
              },
            });
            navigate(link(`/tv-brands/${result.data?.duplicateTvBrand?.tvBrand.id}`));
            moreMenuProps.onClose();
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>{t('pages.tv_brands.list.menu.duplicate')}</ListItemText>
        </MenuItem>
        <Divider />

        <MoreDeleteAction
          tooltip={tvBrand.canDestroy.reasons?.fullMessages[0]}
          disabled={!tvBrand.canDestroy.value}
          {...moreActionProps}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            await deleteTvBrand({
              variables: { input: { id: moreMenuProps.context.id } },
            });
            moreMenuProps.onClose();
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.tv_brands.list.menu.delete_confirm')}
        deleteConfirm
        prompt={
          <Trans
            i18nKey="pages.tv_brands.list.menu.delete_prompt"
            components={{ b: <b />, span: <span /> }}
          />
        }
        title={t('pages.tv_brands.list.menu.delete_title')}
      />
    </>
  );
};
